import React, { useState } from 'react';
import { Gift, Target, Sparkles, Heart, Star, TrendingUp, CheckCircle, Award, ShoppingCart, Zap } from 'lucide-react';

const Page200500 = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const giftCategories = [
    { id: 'quality', name: 'Kvalitetsprodukter', icon: Star, color: 'blue' },
    { id: 'experiences', name: 'Opplevelser', icon: Heart, color: 'red' },
    { id: 'technology', name: 'Teknologi & Gadgets', icon: Zap, color: 'purple' },
    { id: 'lifestyle', name: 'Livsstil & Velvære', icon: Sparkles, color: 'pink' },
    { id: 'hobbies', name: 'Hobbyer & Interesser', icon: Target, color: 'green' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-accent-50 via-indigo-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">💎</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver 200-500 kr</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Det perfekte søte punktet for gjennomtenkte gaver! Her finner du kvalitetsprodukter og 
              meningsfulle opplevelser som viser at du bryr deg uten å sprenge budsjettet.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-accent-500 to-indigo-600 text-white rounded-lg hover:from-accent-600 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn gaver i denne prisklassen
            </button>
          </div>
        </div>
      </section>

      {/* Sweet Spot Philosophy */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hvorfor 200-500 kr er det perfekte gavesegmentet
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <CheckCircle className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Kvalitet som holder</h3>
                <p className="text-secondary-600">
                  I denne prisklassen får du produkter med ekte kvalitet som varer. Det er ikke lenger 
                  "billig og cheerful", men heller ikke luksus-segmentet. Perfekt balanse.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-primary-50 rounded-lg p-6 border border-purple-100">
                <Heart className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Passende for de fleste</h3>
                <p className="text-secondary-600">
                  Denne prisklassen fungerer for både nære venner, familiemedlemmer og kolleger. 
                  Det er nok til å vise at du bryr deg, men ikke så mye at det blir ukomfortabelt.
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Target className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Mangfoldig utvalg</h3>
                <p className="text-secondary-600">
                  Her åpner det seg et hav av muligheter - fra fine opplevelser til kvalitetsprodukter, 
                  teknologi og personlige gaver. Du har rom til å være kreativ.
                </p>
              </div>
            </div>

            <div className="bg-warning-50 border border-warning-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Award className="w-6 h-6 text-warning-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-warning-800 mb-2">Den gylne middelvei</h3>
                  <p className="text-warning-700">
                    200-500 kr er ofte det som føles "riktig" for bursdagsgaver til venner, julegaver til 
                    familie og anerkjennelsesgaver til kolleger. Det er tilstrekkelig til å kjøpe noe 
                    virkelig fint, men ikke så mye at det skaper forventningspress.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Exploration */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk gavekategorier i denne prisklassen
            </h2>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-accent-500 text-white border-accent-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-accent-300'
                }`}
              >
                Alle kategorier
              </button>
              {giftCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? `bg-${category.color}-500 text-white border-${category.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Quality Products */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'quality' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Star className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Kvalitetsprodukter</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🏠 For hjemmet (250-450 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Designerkopper og kaffeservise</li>
                      <li>• Kvalitetskjøkkenredskaper</li>
                      <li>• Dekorative vaser og lysestaker</li>
                      <li>• Premium sengetøy og håndklær</li>
                      <li>• Aromaterapi og hjemme-spa produkter</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">👔 Personlig stil (200-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitetsparfyme og hudpleieprodukter</li>
                      <li>• Stilfulle tilbehør og smykker</li>
                      <li>• Lær-lommebøker og vesker</li>
                      <li>• Kvalitets solbriller</li>
                      <li>• Designerbefredere og skjerf</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-accent-50 p-3 rounded text-xs text-accent-700">
                  ⭐ Tips: I denne prisklassen kan du velge etablerte merker som er kjent for kvalitet
                </div>
              </div>

              {/* Experiences */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'experiences' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Heart className="w-8 h-8 text-error-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Opplevelser</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-error-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎭 Kultur og underholdning (200-400 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Konsert- og teaterbilletter</li>
                      <li>• Museum og utstillingsbesøk</li>
                      <li>• Kinobilletter med middag</li>
                      <li>• Guidede byvandringer</li>
                      <li>• Workshops og kurs</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-error-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🍽️ Mat og drikke (250-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Restaurantbesøk på fine steder</li>
                      <li>• Vin- eller ølsmaking</li>
                      <li>• Matlagingskurs</li>
                      <li>• Afternoon tea eller brunch</li>
                      <li>• Gourmet-opplevelser og tasting-menyer</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-error-50 p-3 rounded text-xs text-error-700">
                  💝 Tips: Opplevelser skaper minner som varer lenger enn fysiske gjenstander
                </div>
              </div>

              {/* Technology */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'technology' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Zap className="w-8 h-8 text-purple-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Teknologi & Gadgets</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">📱 Praktisk teknologi (200-450 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Trådløse ladepadder og powerbanks</li>
                      <li>• Bluetooth høyttalere</li>
                      <li>• Smart home-gadgets (pærer, kontakter)</li>
                      <li>• Fitness-trackere og smartklokker</li>
                      <li>• Kamera-tilbehør og stativer</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎧 Audio og underholdning (250-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitets ørepropper og hodetelefoner</li>
                      <li>• Portable høyttalere</li>
                      <li>• Gaming-tilbehør</li>
                      <li>• Streaming-enheter</li>
                      <li>• Podcast og audiobook-utstyr</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-purple-50 p-3 rounded text-xs text-purple-700">
                  ⚡ Tips: Teknologi i denne prisklassen har ofte gode anmeldelser og lang holdbarhet
                </div>
              </div>

              {/* Lifestyle & Wellness */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'lifestyle' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Sparkles className="w-8 h-8 text-primary-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Livsstil & Velvære</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🧘 Wellness og selvpleie (200-400 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Yoga-utstyr og meditasjonsputer</li>
                      <li>• Aromaterapi og essential oils-sett</li>
                      <li>• Spa-produkter og badsalt</li>
                      <li>• Mindfulness og selvhjelp-bøker</li>
                      <li>• Planter og potter for grønt miljø</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-primary-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">☕ Livsstil og komfort (250-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Premium kaffe og te-samlinger</li>
                      <li>• Varme pledd og kashmere-produkter</li>
                      <li>• Designerbeholdere og oppbevaring</li>
                      <li>• Kvalitets reise-tilbehør</li>
                      <li>• Abonnementer og medlemskap</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-primary-50 p-3 rounded text-xs text-primary-700">
                  ✨ Tips: Livsstil-gaver viser at du tenker på deres velvære og lykke
                </div>
              </div>

              {/* Hobbies */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'hobbies' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Target className="w-8 h-8 text-success-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Hobbyer & Interesser</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🎨 Kreativitet og kunst (200-450 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Profesjonelle kunst- og tegnemateriell</li>
                      <li>• Håndverks-sett og verktøy</li>
                      <li>• Fotografiutstyr og tilbehør</li>
                      <li>• Musikkinstrument-tilbehør</li>
                      <li>• Kreative workshop-kurs</li>
                    </ul>
                  </div>
                  
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800 mb-2">🏃 Sport og aktivitet (250-500 kr)</h4>
                    <ul className="text-sm text-secondary-600 space-y-1">
                      <li>• Kvalitets treningsutstyr</li>
                      <li>• Sport-spesifikke tilbehør</li>
                      <li>• Utendørs aktivitetsutstyr</li>
                      <li>• Fitness-abonnement eller timer</li>
                      <li>• Reise og eventyr-utstyr</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6 bg-success-50 p-3 rounded text-xs text-success-700">
                  🎯 Tips: Hobbygaver viser at du støtter deres lidenskap og interesser
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasion Guidelines */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Når passer 200-500 kr gaver best?
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-accent-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🎂 Bursdager</h3>
                <p className="text-secondary-600 mb-3">
                  Perfekt for voksne venner, familiemedlemmer og kolleger du har et godt forhold til.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Ideelt for:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Nære venner og familie</li>
                      <li>• Milepæl-bursdager (30, 40, 50)</li>
                      <li>• Kolleger du kjenner godt</li>
                      <li>• Partner-gaver (som tillegg til hovedgave)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Eksempler:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Opplevelse-gaver</li>
                      <li>• Kvalitets livsstilsprodukter</li>
                      <li>• Hobby-relaterte gaver</li>
                      <li>• Premium mat og drikke</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-success-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🎄 Jul og høytider</h3>
                <p className="text-secondary-600 mb-3">
                  Den perfekte prisklassen for de fleste julegaver til voksne familiemedlemmer.
                </p>
                <div className="text-sm">
                  <p><strong>Passer spesielt godt til:</strong> Søsken, foreldre (hvis fellesgave), 
                  svigerforeldre, onkler/tanter, og familie-venner. Denne prisklassen 
                  tillater deg å gi noe virkelig fint uten å gå over toppen.</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">💼 Profesjonelle anledninger</h3>
                <p className="text-secondary-600 mb-3">
                  Takk-gaver, avskjed fra jobben eller profesjonell anerkjennelse.
                </p>
                <div className="text-sm">
                  <p><strong>Eksempler:</strong> Takk til mentor, avskjedsgave til kollega som slutter, 
                  anerkjennelse for ekstra innsats, eller som fellesgave fra teamet til lederen.</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-accent-50 border border-accent-200 rounded-lg p-6">
              <TrendingUp className="w-6 h-6 text-accent-600 mb-2" />
              <h3 className="text-lg font-semibold text-secondary-800 mb-2">Smart shopping i denne prisklassen</h3>
              <p className="text-secondary-700 text-sm">
                Med 200-500 kr har du råd til å velge kvalitet og gjøre grundig research. 
                Les anmeldelser, sammenlign alternativer og vurder langsiktig verdi. 
                Du kan også kombinere flere mindre ting til et gjennomtenkt gavesett.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Shopping Tips */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Få mest mulig ut av 200-500 kr
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <ShoppingCart className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Research lønner seg</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Les anmeldelser på flere plattformer</li>
                  <li>• Sammenlign priser hos ulike forhandlere</li>
                  <li>• Sjekk om det finnes nyere modeller</li>
                  <li>• Se etter sesongrabatter og tilbud</li>
                  <li>• Vurder garanti og returpolicy</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <Target className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Personlig tilpasning</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Velg basert på deres livsstil</li>
                  <li>• Tenk på deres eksisterende utstyr</li>
                  <li>• Vurder årstid og timing</li>
                  <li>• Observer hva de bruker daglig</li>
                  <li>• Spør diskret om ønsker</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <Sparkles className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Verdiøkning</h3>
                <ul className="text-sm text-secondary-600 space-y-2">
                  <li>• Kombiner flere relaterte småting</li>
                  <li>• Legg til personlig tilpasning</li>
                  <li>• Inkluder gjennomtenkt innpakning</li>
                  <li>• Skriv en meningsfull hilsen</li>
                  <li>• Tenk på fremtidig bruksverdi</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-accent-500 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne den perfekte gaven i denne prisklassen?
            </h2>
            <p className="text-xl text-accent-100 mb-8">
              Bruk vår gavefinner for å oppdage kvalitetsgaver som passer ditt budsjett
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-accent-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('price')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-accent-600 transition-colors font-semibold"
              >
                Se andre prisklasser
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Page200500;